/* eslint-disable no-console */
/* eslint-disable implicit-arrow-linebreak */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Col, Label, Row, TextField } from '@folio/stripes-components';
import { FormattedMessage, useIntl } from 'react-intl';
import { Field, useField, useForm } from 'react-final-form';
import Barcode from 'react-barcode';
import { memoize } from 'lodash';
import { useStripes } from '@folio/stripes/core';
import { composeValidators, required } from '../../../../utils/utils';
import HelpeModal from '../../../HelpeModal';
import css from '../../styles.css';
import useLocationName from '../../../../hooks/useLocationName';
import useBarcodeGenerator from '../../../../hooks/Barcode/useBarcodeGenerator';
import { useBarcodeSession } from '../../../../contexts/BarcodeSessionContext';

function checkUniqueBarcode(okapi, barcode) {
  return fetch(`${okapi.url}/inventory/items?query=(barcode=="${barcode}")`, {
    headers: {
      'X-Okapi-Tenant': okapi.tenant,
      'Content-Type': 'application/json',
      ...(okapi.token && { 'X-Okapi-Token': okapi.token }),
    },
    credentials: 'include',
  });
}

function validateBarcode(okapi, barcodeInitvalue) {
  return memoize(async (barcode) => {
    // Skip validation for empty/null values or if barcode hasn't changed from initial
    if (!barcode || barcode.trim() === '') return '';
    if (barcodeInitvalue && barcode === barcodeInitvalue) return '';

    const error = <FormattedMessage id="ui-inventory.barcodeTaken" />;
    const response = await checkUniqueBarcode(okapi, barcode);

    if (response.status >= 400) {
      return error;
    }

    const json = await response.json();

    if (json.totalRecords > 0) {
      return error;
    }

    return '';
  });
}

const propTypes = {
  fieldValues: PropTypes.object,
  index: PropTypes.number,
  fieldName: PropTypes.string,
  parentField: PropTypes.object,
  showHelpeIcon: PropTypes.bool,
  controlledVocab: PropTypes.object,
};

function BarcodeField({
  fieldValues,
  index,
  fieldName,
  parentField,
  showHelpeIcon,
  controlledVocab,
}) {
  const { okapi } = useStripes();
  const { locale } = useIntl();
  const { checkBarcodeExists } = useBarcodeSession();

  const barcodeField = useField(fieldName);

  const itemLocationField = useField(
    fieldName.replace('barcode', 'permanentLocation'),
  );

  const form = useForm();

  const permanentLocationId = itemLocationField.input.value?.id;
  const { locationCode } = useLocationName(
    permanentLocationId,
    controlledVocab,
  );

  const templatesConfigs = controlledVocab.templatesConfigs;

  const {
    configValue: { enableGenerator, showBarcodeLabel },
  } = templatesConfigs.find(
    (config) => config.configName === 'generatorSettings',
  );

  const { generateBarcodeValue } = useBarcodeGenerator();
  const [barcodeError, setBarcodeError] = useState(false);

  // Validation functions for barcode field
  const validateBarcodeLength = (value) => {
    if (!value || value.trim() === '') return undefined; // Ignore empty values

    if (value.trim().length < 3) {
      return (
        <FormattedMessage
          id="ui-plugin-inventory-templates.barcode.validation.tooShort"
          defaultMessage="Barcode must be at least 3 characters long"
        />
      );
    }

    return undefined;
  };

  const validateBarcodeFormat = (value) => {
    if (!value || value.trim() === '') return undefined; // Ignore empty values

    const barcodeRegex = /^[A-Za-z0-9-_]+$/;
    if (!barcodeRegex.test(value.trim())) {
      return (
        <FormattedMessage
          id="ui-plugin-inventory-templates.barcode.validation.invalidCharacters"
          defaultMessage="Barcode contains invalid characters"
        />
      );
    }

    return undefined;
  };

  const validateBarcodeSession = (value) => {
    if (!value || value.trim() === '') return undefined; // Ignore empty values
    if (value === barcodeField.meta.initial) return undefined; // Ignore initial value

    if (checkBarcodeExists(value.trim())) {
      return (
        <FormattedMessage
          id="ui-plugin-inventory-templates.barcode.validation.sessionDuplicate"
          defaultMessage="This barcode conflicts with another item in the current session"
        />
      );
    }

    return undefined;
  };

  const { label, type, isRequired, canEdit, isRepeatable, helpeText } =
    fieldValues;
  const fieldLabel = label.find((field) => field.locale === locale)?.value;

  const helpeCondition =
    showHelpeIcon &&
    parentField?.type !== 'fieldArray' &&
    !isRepeatable &&
    helpeText;
  const containerCondition =
    parentField?.type !== 'fieldArray' && !isRepeatable;

  const handleFieldLabelAppeerance = () => {
    if (isRepeatable || (parentField?.type === 'fieldArray' && index !== 0)) {
      return null;
    }

    return <Label fullWidth>{fieldLabel}</Label>;
  };

  const onGenerateBarcodeValue = () => {
    if (permanentLocationId) {
      form.change(
        fieldName,
        generateBarcodeValue(permanentLocationId, locationCode),
      );
    } else {
      setBarcodeError(true);
    }
  };

  useEffect(() => {
    if (permanentLocationId || barcodeField.meta.touched) {
      setBarcodeError(false);
    }
  }, [barcodeField.meta.touched, permanentLocationId]);

  return (
    <Row className={containerCondition ? css.container : ''}>
      <Col xs={helpeCondition ? 5 : 6}>
        <Field
          id={`inventory-templates-form-generator-${type}-field-${index}`}
          component={TextField}
          fullWidth
          name={fieldName}
          label={handleFieldLabelAppeerance()}
          required={isRequired}
          validate={composeValidators(
            isRequired ? required : undefined,
            validateBarcodeLength,
            validateBarcodeFormat,
            validateBarcodeSession,
            validateBarcode(okapi, barcodeField.meta.initial),
          )}
          type="text"
          disabled={!canEdit}
          onFocus={() => setBarcodeError(false)}
          parse={(value) => (value === '' ? null : value)} // Fix issue: Empty values don't appear in values
        />
        {barcodeError && (
          <span className={css.barcodeValidate}>
            <FormattedMessage
              id="ui-plugin-inventory-templates.fieldGenerator.barcodeField.noLocation.msg"
              defaultMessage="You must select location, to be able to generate barcode"
            />
          </span>
        )}
      </Col>
      {enableGenerator && (
        <Col xs={2} className={css.margin_btn}>
          <Button
            id="fieldGenerator-barcodeField-generate-btn"
            buttonStyle="primary"
            onClick={() => {
              onGenerateBarcodeValue();
            }}
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            fullWidth
            disabled={barcodeField.input.value}
          >
            <FormattedMessage
              id="ui-plugin-inventory-templates.fieldGenerator.barcodeField.generateBtn.label"
              defaultMessage="Generate"
            />
          </Button>
        </Col>
      )}
      {showBarcodeLabel && barcodeField.input.value && (
        <Col xs={enableGenerator ? 4 : 5} className={css.barcodeCol}>
          <Barcode value={barcodeField.input.value} />
        </Col>
      )}
      {helpeCondition && (
        <Col xs={1} className={css.margin_btn}>
          <HelpeModal
            fieldData={fieldValues}
            title={fieldLabel}
            iconSize="medium"
          />
        </Col>
      )}
    </Row>
  );
}

BarcodeField.propTypes = propTypes;

export default BarcodeField;
